import * as React from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import { DataGrid, gridClasses, GridRowParams } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useNotification } from "../../context/notification-provider";
import { useDialogs } from "../dialog/use-dialog";
import useFilterTypes from "./use-filter-types";
import { getColumns, pageTitle } from "./filter-types-data-grid";
import { setFilterTypeEnabled } from "./service";
import FilterTypeCreateDialog from "./filter-type-create-dialog";
import FilterTypeEditDialog from "./filter-type-edit-dialog";
import FilterTypeDeleteDialog from "./filter-type-delete-dialog";
import FilterOptionsPanel from "./filter-options-panel";
import { FilterTypeDetail } from "./types";

const INITIAL_PAGE_SIZE = 10;

const FilterTypesList = () => {
  const { showNotification } = useNotification();
  const { confirm } = useDialogs();
  const { data, error: loadingError, isLoading, mutate } = useFilterTypes();

  const [dialog, setDialog] = React.useState<"create" | "edit" | "delete" | "">(
    "",
  );
  const [selectedFilterType, setSelectedFilterType] =
    React.useState<FilterTypeDetail | null>(null);
  const [expandedFilterType, setExpandedFilterType] =
    React.useState<FilterTypeDetail | null>(null);

  const initialState = React.useMemo(
    () => ({
      pagination: { paginationModel: { pageSize: INITIAL_PAGE_SIZE } },
    }),
    [],
  );

  const handleCreateClick = React.useCallback(() => {
    setDialog("create");
  }, []);

  const handleOnRefreshClick = React.useCallback(() => {
    showNotification({
      message: "Refreshing filter types...",
      severity: "info",
      duration: 2000,
    });
    mutate();
  }, [showNotification, mutate]);

  const handleOnDialogClose = React.useCallback(() => {
    setDialog("");
    setSelectedFilterType(null);
    mutate();
  }, [mutate]);

  const handleOnEdit = React.useCallback((row: FilterTypeDetail) => {
    setSelectedFilterType(row);
    setDialog("edit");
  }, []);

  const handleOnDelete = React.useCallback((row: FilterTypeDetail) => {
    setSelectedFilterType(row);
    setDialog("delete");
  }, []);

  const handleRowClick = React.useCallback(
    (params: GridRowParams<FilterTypeDetail>) => {
      setExpandedFilterType((previous) =>
        previous?.id === params.row.id ? null : params.row,
      );
    },
    [],
  );

  const handleToggleEnabled = React.useCallback(
    async (filterType: FilterTypeDetail) => {
      const newValue = !filterType.isEnabled;
      const action = newValue ? "enable" : "disable";

      try {
        const result = await confirm(
          `Are you sure you want to ${action} the filter type "${filterType.label}"?`,
          {
            title: "Confirm Filter Type Status Change",
            okText: "Confirm",
            cancelText: "Cancel",
            severity: "warning",
          },
        );

        if (result === true) {
          await setFilterTypeEnabled(filterType.id, newValue);
          showNotification({
            message: `Filter type "${filterType.label}" ${action}d.`,
            severity: "success",
            duration: 3000,
          });
          mutate();
        }
      } catch (toggleError) {
        showNotification({
          message: `Failed to update filter type. ${(toggleError as Error).message}`,
          severity: "error",
          duration: 3000,
        });
      }
    },
    [confirm, showNotification, mutate],
  );

  const refreshedExpandedFilterType = React.useMemo(
    () =>
      expandedFilterType
        ? (data?.find((row) => row.id === expandedFilterType.id) ??
          expandedFilterType)
        : null,
    [data, expandedFilterType],
  );

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Box sx={{ fontWeight: 600 }}>{pageTitle}</Box>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Tooltip title="Reload data" placement="left" enterDelay={1000}>
            <div>
              <IconButton
                size="small"
                aria-label="refresh"
                onClick={handleOnRefreshClick}
              >
                <RefreshIcon />
              </IconButton>
            </div>
          </Tooltip>
          <Button
            variant="contained"
            onClick={handleCreateClick}
            startIcon={<AddIcon />}
          >
            Create
          </Button>
        </Stack>
      </Stack>
      <Box sx={{ flex: 1, width: "100%" }}>
        {loadingError ? (
          <Alert severity="error">Unable to load filter types</Alert>
        ) : (
          <DataGrid
            rows={data || []}
            columns={getColumns({
              onEdit: handleOnEdit,
              onDelete: handleOnDelete,
              onToggleEnabled: handleToggleEnabled,
            })}
            disableRowSelectionOnClick
            onRowClick={handleRowClick}
            loading={isLoading}
            initialState={initialState}
            pageSizeOptions={[5, INITIAL_PAGE_SIZE, 25]}
            sx={{
              height: "500px",
              [`& .${gridClasses.row}:hover`]: {
                cursor: "pointer",
              },
            }}
          />
        )}
      </Box>
      {refreshedExpandedFilterType && (
        <FilterOptionsPanel
          filterType={refreshedExpandedFilterType}
          onFilterTypeMutate={mutate}
        />
      )}
      {dialog === "create" && (
        <FilterTypeCreateDialog
          open={dialog === "create"}
          onClose={handleOnDialogClose}
        />
      )}
      {dialog === "edit" && selectedFilterType && (
        <FilterTypeEditDialog
          selectedFilterType={selectedFilterType}
          open={dialog === "edit"}
          onClose={handleOnDialogClose}
        />
      )}
      {dialog === "delete" && selectedFilterType && (
        <FilterTypeDeleteDialog
          selectedFilterType={selectedFilterType}
          open={dialog === "delete"}
          onClose={handleOnDialogClose}
        />
      )}
    </Box>
  );
};

export default FilterTypesList;
